import React, { useState, useEffect } from 'react';
import { Star, Play } from 'lucide-react';
import { ApiService } from '../services/api';
import { SubscriptionPlan, TutorialVideo } from '../types';
import VideoPlayerModal from './VideoPlayerModal';

interface ProFeatureBadgeProps {
    featureName: string; // e.g. "map::Lee/Luv Simulation" or "map::Streckenvarianten"
    className?: string;
    compact?: boolean;
    overrideLabel?: string;
}

const ProFeatureBadge: React.FC<ProFeatureBadgeProps> = ({ featureName, className = '', compact = false, overrideLabel }) => {
    const [linkedVideo, setLinkedVideo] = useState<TutorialVideo | null>(null);
    const [playing, setPlaying] = useState(false);
    const [loading, setLoading] = useState(true);
    const [debugError, setDebugError] = useState("Loading...");

    useEffect(() => {
        const checkFeatureVideo = async () => {
            try {
                const searchStr = featureName.toLowerCase().replace('map::', '').replace(/\s+/g, '').trim();
                console.log(`[ProFeatureBadge] Checking video for feature: ${searchStr}`);

                // Fetch plans and tutorials in parallel
                const [plans, tutsRes] = await Promise.all([
                    ApiService.getSubscriptionPlans().catch(() => []),
                    ApiService.apiRequest('/tutorials/all').catch(() => null)
                ]);

                if (!plans || plans.length === 0) {
                    setDebugError("No plans");
                    setLoading(false);
                    return;
                }

                let videoId: string | null = null;
                
                // Find the feature in any of the plans
                for (const plan of plans as SubscriptionPlan[]) {
                    if (plan.features) {
                        const foundFeature = plan.features.find(f => f.toLowerCase().replace(/\s+/g, '').includes(searchStr));
                        if (foundFeature && foundFeature.includes('|vid:')) {
                            videoId = foundFeature.split('|vid:')[1];
                            console.log(`[ProFeatureBadge] Found video ID ${videoId} for feature ${searchStr} in plan ${plan.name}`);
                            break; // Stop looking once we find it
                        }
                    }
                }

                if (videoId && tutsRes && tutsRes.ok) {
                    const tutorials: TutorialVideo[] = await tutsRes.json();
                    const video = tutorials.find(t => t.id === videoId);
                    if (video) {
                        console.log(`[ProFeatureBadge] Successfully linked video "${video.title}"`);
                        setLinkedVideo(video);
                        setDebugError("");
                    } else {
                        setDebugError("Video not found");
                        console.warn(`[ProFeatureBadge] Video ID ${videoId} not found in /tutorials/all! Is it set to "Sichtbar" (Visible)?`);
                    }
                } else if (videoId) {
                    setDebugError("tutsRes failed");
                    console.warn(`[ProFeatureBadge] Failed to fetch tutorials list to resolve video ID ${videoId}.`);
                } else {
                    setDebugError("No videoId");
                    console.log(`[ProFeatureBadge] No video ID found for feature ${searchStr} in any subscription plan.`);
                }
            } catch (err) {
                setDebugError("Error thrown");
                console.error("[ProFeatureBadge] Failed to fetch feature video", err);
            } finally {
                setLoading(false);
            }
        };

        checkFeatureVideo();
    }, [featureName]);

    return (
        <>
            {compact ? (
                <span 
                    className={`px-1 py-0.5 bg-yellow-500 text-black text-[8px] font-black rounded uppercase tracking-wider shadow-md shadow-yellow-500/20 flex items-center gap-0.5 ${linkedVideo ? 'cursor-pointer hover:bg-yellow-400 hover:scale-105 transition-all' : ''} ${className}`}
                    onClick={(e) => {
                        if (linkedVideo) {
                            e.stopPropagation();
                            setPlaying(true);
                        }
                    }}
                    title={linkedVideo ? "Tutorial Video ansehen" : (overrideLabel || "PRO Feature")}
                >
                    ★ {overrideLabel ? overrideLabel.replace(/\s*feature\s*/i, '') : 'PRO'}
                    {linkedVideo && <Play className="w-2.5 h-2.5 ml-0.5 text-black/80" fill="currentColor" />}
                    {!linkedVideo && <span className="ml-1 text-[7px] text-red-700 bg-red-100 px-0.5 rounded opacity-50">{debugError}</span>}
                </span>
            ) : (
                <span 
                    className={`px-3 py-1 bg-amber-500 text-black text-[10px] font-black rounded-lg uppercase tracking-wider shadow-lg shadow-amber-500/20 flex items-center gap-1 ${linkedVideo ? 'cursor-pointer hover:bg-amber-400 hover:scale-105 transition-all outline outline-2 outline-offset-2 outline-amber-500/30' : ''} ${className}`}
                    onClick={(e) => {
                        if (linkedVideo) {
                            e.stopPropagation();
                            setPlaying(true);
                        }
                    }}
                    title={linkedVideo ? "Tutorial Video ansehen" : (overrideLabel || "PRO Feature")}
                >
                    <Star className="w-3 h-3" /> {overrideLabel || "PRO Feature"}
                    {linkedVideo && (
                        <span className="flex items-center gap-1 bg-black/10 rounded-full px-1.5 py-0.5 ml-1 border border-black/10">
                            <Play className="w-2.5 h-2.5 text-black/80" fill="currentColor" />
                            <span className="text-[8px] font-bold">VIDEO</span>
                        </span>
                    )}
                    {!linkedVideo && <span className="ml-1 text-[8px] text-red-700 bg-red-100 px-1 rounded opacity-50">{debugError}</span>}
                </span>
            )}

            {playing && linkedVideo && (
                <VideoPlayerModal 
                    video={linkedVideo} 
                    onClose={() => setPlaying(false)} 
                />
            )}
        </>
    );
};

export default ProFeatureBadge;
